import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// Generates a one-time Telegram binding link + QR. For a client (company) it also
// lists/revokes the company's bound Telegram accounts. For admin self-bind pass `admin`.
export default function TelegramBind({ clientId, admin, compact, iconOnly }: { clientId?: string; admin?: boolean; compact?: boolean; iconOnly?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<any>(null);
  const [bindings, setBindings] = useState<any[]>([]);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  const showList = !!clientId || !!admin;

  const load = async (force?: boolean) => {
    setLoading(true); setErr('');
    try {
      const st: any = await api.tgStatus(); setConfigured(!!st.configured);
      const d: any = await api.tgBindToken(admin ? 'admin' : 'client', clientId, force);
      setData(d); setConfigured(!!d.configured);
      if (showList) { const b: any = await api.tgBindings(admin ? { kind: 'admin' } : { clientId }); setBindings(b.rows || []); }
    } catch (e: any) { setErr(e.message); } finally { setLoading(false); }
  };
  const openPanel = () => { setOpen(true); setData(null); load(); };
  const revoke = async (id: string) => {
    if (!window.confirm(t('tg_revoke_confirm'))) return;
    try { await api.tgRevoke(id); setBindings(prev => prev.filter(b => b.id !== id)); } catch (e: any) { setErr(e.message); }
  };
  const copy = () => { if (data?.link) { navigator.clipboard?.writeText(data.link); setCopied(true); setTimeout(() => setCopied(false), 1500); } };

  const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: compact ? '5px 10px' : '7px 12px', borderRadius: 8, border: '1px solid #229ED9', background: 'transparent', color: '#229ED9', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' };

  return (
    <>
      {iconOnly ? (
        <button type="button" onClick={openPanel} title={t('tg_title')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#229ED9', padding: 4, display: 'inline-flex' }}>
          <i className="ti ti-brand-telegram" style={{ fontSize: 18 }} />
        </button>
      ) : (
        <button type="button" onClick={openPanel} style={btn}>
          <i className="ti ti-brand-telegram" style={{ fontSize: 15 }} />{admin ? t('tg_bind_me') : t('tg_bind_client')}
        </button>
      )}
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-surface)', borderRadius: 14, padding: 22, width: 380, maxWidth: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <i className="ti ti-brand-telegram" style={{ fontSize: 20, color: '#229ED9' }} />
              <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{t('tg_title')}</div>
              <div style={{ flex: 1 }} />
              <button onClick={() => setOpen(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 18 }}><i className="ti ti-x" /></button>
            </div>

            {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>{err}</div>}
            {loading ? <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div>
              : !configured ? <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '10px 0' }}>{t('tg_not_configured')}</div>
              : (
                <>
                  <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 12 }}>{admin ? t('tg_scan_hint_admin') : t('tg_scan_hint')}</div>
                  {data?.qrDataUrl
                    ? <div style={{ textAlign: 'center' }}><img src={data.qrDataUrl} alt="QR" style={{ width: 220, height: 220, borderRadius: 10, border: '1px solid var(--border)' }} /></div>
                    : <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('tg_not_configured')}</div>}
                  {data?.link && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                      <input readOnly value={data.link} style={{ flex: 1, fontSize: 11.5, padding: '7px 9px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)' }} />
                      <button onClick={copy} style={{ ...btn, border: 'none', background: '#229ED9', color: '#fff' }}>{copied ? t('tg_copied') : t('tg_copy_link')}</button>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flex: 1 }}>{t(admin ? 'tg_expires_admin' : 'tg_expires')}</span>
                    <button onClick={() => load(true)} style={{ border: 'none', background: 'none', color: '#229ED9', cursor: 'pointer', fontSize: 11.5, fontWeight: 600 }}><i className="ti ti-refresh" style={{ marginRight: 3 }} />{t('tg_regenerate')}</button>
                  </div>

                  {showList && (
                    <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>{t(admin ? 'tg_bound_admins' : 'tg_bound_accounts')}</div>
                      {bindings.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('tg_none_bound')}</div>
                        : bindings.map(b => (
                          <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                            <i className="ti ti-user-circle" style={{ color: 'var(--text-tertiary)' }} />
                            <span style={{ fontSize: 12.5, color: 'var(--text-primary)', flex: 1 }}>{b.tg_name || b.tg_username || b.chat_id}{b.tg_username ? ` (@${b.tg_username})` : ''}</span>
                            <button onClick={() => revoke(b.id)} style={{ border: 'none', background: 'none', color: '#E24B4A', cursor: 'pointer', fontSize: 11.5 }}>{t('tg_revoke')}</button>
                          </div>
                        ))}
                    </div>
                  )}
                </>
              )}
          </div>
        </div>
      )}
    </>
  );
}
